import { useMemo } from 'react';
import { graphql } from 'react-relay';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { type useIsHighlightOptionPanelEnabledQuery$data } from './__generated__/useIsHighlightOptionPanelEnabledQuery.graphql.ts';

export const useIsHighlightOptionPanelEnabled = (): {
	isHighlightOptionPanelEnabled: boolean;
} => {
	const { userId } = useSessionData();
	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, loading, error } = useQueryInterop<useIsHighlightOptionPanelEnabledQuery$data>(
		graphql`
			query useIsHighlightOptionPanelEnabledQuery {
				userPreferences {
					highlightOptionPanelEnabled
				}
			}
		`,
		{
			fetchPolicy: 'cache-and-network',
			skip: !userId,
		},
	);

	return useMemo(() => {
		// We always want to default to true until we get a result
		let isHighlightOptionPanelEnabled = true;

		if (error) {
			// We always want to handle errors caused from this, just default to existing behavior
			markErrorAsHandled(error);
		}

		if (!loading && data?.userPreferences) {
			isHighlightOptionPanelEnabled = data?.userPreferences.highlightOptionPanelEnabled;
		}

		return { isHighlightOptionPanelEnabled };
	}, [data, loading, error]);
};
